/**
 * GameEngine — PixiJS v8 기반 공통 후원 게임 엔진.
 * Plan 08-02 Task 1 (GAME-01).
 *
 * 책임:
 *   1) PixiJS Application bootstrap (async `app.init({...})` — v8 breaking, Research §Pitfall 3).
 *   2) 현재 등록된 Game 관리 — registerGame 시 이전 destroy → 신규 init.
 *   3) DonationEvent 수신 → state machine (idle/spinning/result) 전이 + Game.onDonation dispatch.
 *   4) eventId dedupe (set 상한 256, FIFO eviction) — T-08-02-02.
 *   5) spinning 중 새 donation → pending queue (상한 100) — T-08-02-03 DoS 방어.
 *   6) result 30s 후 자동 idle + queue consume.
 *   7) destroy 시 Pixi app.destroy + 내부 자료 구조 clear.
 *
 * 테스트 격리:
 *   - PIXI.Application init 은 실 WebGL 필요 → jsdom 환경에선 실행 불가.
 *   - `CreateGameEngineOptions.pixiFactory` 로 stub 주입 가능 (factory 가 Application-like 반환).
 *   - 실 Pixi 부팅은 E2E (Playwright) 에서 검증.
 */
import type { Application, Container } from 'pixi.js';
import { Application as PixiApplication, Container as PixiContainer } from 'pixi.js';
import {
  assertGameMeta,
  type DonationEvent,
  type Game,
  type GameContext,
  type GameResult,
} from './game-interface.js';
import { canTransition, type GameState } from './game-state.js';

/** 내부 상수 — T-08-02-03 DoS 완화 + Research §Pitfall. */
const DEDUPE_CAP = 256;
const PENDING_CAP = 100;
const DEFAULT_RESULT_IDLE_DELAY_MS = 30_000;

export interface CreateGameEngineOptions {
  readonly reducedMotion: boolean;
  /**
   * Pixi Application 생성/초기화 주입점. 테스트에서 stub 제공.
   * 기본값: 실 `pixi.js@8` Application.init.
   */
  readonly pixiFactory?: (canvas: HTMLCanvasElement) => Promise<Application>;
  /** result 후 자동 idle 복귀 delay — 테스트에서 짧게 주입. */
  readonly resultIdleDelayMs?: number;
}

/** 기본 Pixi bootstrap — PixiJS v8 async init 패턴. */
async function defaultPixiFactory(canvas: HTMLCanvasElement): Promise<Application> {
  const app = new PixiApplication();
  await app.init({
    canvas,
    resizeTo: canvas.parentElement ?? globalThis.window,
    antialias: true,
    background: 'transparent',
    backgroundAlpha: 0,
  });
  return app;
}

export class GameEngine {
  private readonly app: Application;
  private readonly stage: Container;
  private readonly reducedMotion: boolean;
  private readonly resultIdleDelayMs: number;

  private currentGame: Game | null = null;
  private state: GameState = 'idle';

  /** 동일 eventId 재처리 방지. Size cap = DEDUPE_CAP, FIFO eviction. */
  private readonly seen: Set<string> = new Set();
  /** spinning 중 도착한 donation — result 이후 consume. */
  private readonly pending: DonationEvent[] = [];
  /** pending drain / idle-timer 를 위한 timer handle. */
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    app: Application,
    opts: Pick<CreateGameEngineOptions, 'reducedMotion' | 'resultIdleDelayMs'>,
  ) {
    this.app = app;
    // Game 전용 stage — 엔진 소유의 중간 Container 를 두어
    // destroy 시 stage 를 통째로 제거할 수 있도록 격리.
    this.stage = new PixiContainer();
    app.stage.addChild(this.stage);
    this.reducedMotion = opts.reducedMotion;
    this.resultIdleDelayMs = opts.resultIdleDelayMs ?? DEFAULT_RESULT_IDLE_DELAY_MS;
  }

  getState(): GameState {
    return this.state;
  }

  getCurrentGameKind(): string | null {
    return this.currentGame?.meta.kind ?? null;
  }

  async registerGame(game: Game): Promise<void> {
    if (this.destroyed) throw new Error('GameEngine destroyed');
    assertGameMeta(game.meta);

    // 기존 game 있으면 destroy (idempotent 교체).
    if (this.currentGame) {
      try {
        this.currentGame.destroy();
      } catch (err) {
        console.warn('[game-engine] previous game destroy failed', err);
      }
    }

    // 이전 게임이 남긴 render object 클리어.
    this.stage.removeChildren();

    const ctx: GameContext = {
      stage: this.stage,
      app: this.app,
      reducedMotion: this.reducedMotion,
      reportResult: (result: GameResult): void => this.onGameResult(result),
      config: undefined,
    };

    this.currentGame = game;
    await game.init(ctx);
  }

  async dispatchDonation(event: DonationEvent): Promise<void> {
    if (this.destroyed) return;

    // 1) dedupe — T-08-02-02.
    if (this.seen.has(event.eventId)) {
      return;
    }
    this.addSeen(event.eventId);

    // 2) 등록된 game 이 없으면 drop (경고 로그).
    if (!this.currentGame) {
      console.warn('[game-engine] donation dropped — no game registered', event.eventId);
      return;
    }

    // 3) spinning 중이면 pending queue.
    if (this.state === 'spinning' || this.state === 'result') {
      if (this.pending.length >= PENDING_CAP) {
        // Drop oldest — overflow 는 T-08-02-03 무한 증가 차단.
        this.pending.shift();
      }
      this.pending.push(event);
      return;
    }

    // 4) idle → spinning.
    await this.runDonation(event);
  }

  private async runDonation(event: DonationEvent): Promise<void> {
    if (!this.currentGame) return;
    if (!canTransition(this.state, 'spinning')) {
      // 방어적 — runDonation 은 idle 에서만 호출됨.
      return;
    }
    this.state = 'spinning';
    try {
      await this.currentGame.onDonation(event);
    } catch (err) {
      console.warn('[game-engine] game.onDonation failed', err);
    }
    // game 이 reportResult 를 호출하지 않아도 엔진이 result 로 전이.
    if (this.state === 'spinning') {
      this.transitionToResult();
    }
  }

  private onGameResult(_result: GameResult): void {
    // game 이 명시적으로 라운드 종료 — state 를 result 로 진행.
    if (this.state === 'spinning') {
      this.transitionToResult();
    }
  }

  private transitionToResult(): void {
    if (!canTransition(this.state, 'result')) return;
    this.state = 'result';
    // reducedMotion 이면 즉시 idle — 연출 건너뜀. 그 외엔 설정된 delay 후 idle.
    const delay = this.reducedMotion ? 0 : this.resultIdleDelayMs;
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => this.transitionToIdle(), delay);
  }

  private transitionToIdle(): void {
    if (this.destroyed) return;
    if (!canTransition(this.state, 'idle')) return;
    this.state = 'idle';
    this.clearIdleTimer();
    // pending drain — 오래된 순서대로 1 건 consume.
    const next = this.pending.shift();
    if (next) {
      void this.runDonation(next);
    }
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private addSeen(id: string): void {
    if (this.seen.size >= DEDUPE_CAP) {
      // FIFO eviction — Set insertion order 로 oldest 제거.
      const oldest = this.seen.values().next().value;
      if (typeof oldest === 'string') this.seen.delete(oldest);
    }
    this.seen.add(id);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearIdleTimer();
    this.pending.length = 0;
    this.seen.clear();
    if (this.currentGame) {
      try {
        this.currentGame.destroy();
      } catch (err) {
        console.warn('[game-engine] current game destroy failed', err);
      }
      this.currentGame = null;
    }
    try {
      this.app.destroy(true, { children: true });
    } catch (err) {
      console.warn('[game-engine] pixi app destroy failed', err);
    }
  }
}

/**
 * Factory — async Pixi init 을 래핑한 뒤 GameEngine 반환.
 * main.ts bootstrap + test stub 양쪽이 사용.
 */
export async function createGameEngine(
  canvas: HTMLCanvasElement,
  opts: CreateGameEngineOptions,
): Promise<GameEngine> {
  const factory = opts.pixiFactory ?? defaultPixiFactory;
  const app = await factory(canvas);
  return new GameEngine(app, {
    reducedMotion: opts.reducedMotion,
    resultIdleDelayMs: opts.resultIdleDelayMs,
  });
}
